import React from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { ContainerProps } from './types';

// Styled Components
import { Container, Question, Actions, YesButton, NoButton } from './styles';

/**
 * «¿Has cambiado de sentido?» — el aviso del detector automático.
 *
 * Era una tarjeta incrustada en la pantalla del recorrido. Pasa a toast porque
 * el detector dejó de vivir en esa pantalla: ahora corre en toda la app, así
 * que ya no hay página donde pintarlo (spec 2026-09-01).
 *
 * ── Las tres salidas, y por qué esto NO es una confirmación ────────────────
 *
 *   Sí      → registra el corte ahora
 *   No      → rechaza este extremo y calla el detector 1 km
 *   ignorar → el detector acumula evidencia y lo registra SOLO a los ~500 m
 *
 * 🔴 «Sí» e «ignorar» producen EL MISMO REGISTRO: los dos caminos ejecutan con
 * `state.extremePivot`, o sea mismo pivote, mismas coordenadas y misma hora.
 * «Sí» solo lo adelanta. Por eso esto no son dos salidas simétricas sino UNA
 * acción con consecuencia («No») y un acelerador («Sí») — y por eso no pasa
 * nada si el aviso se va sin respuesta.
 *
 * Eso también es lo que justifica desviarse de la regla de Material («para dos
 * o más acciones, usa un diálogo»): esa regla existe porque dos acciones
 * implican una decisión que se pierde si el snackbar desaparece. Aquí no se
 * pierde nada. Y un diálogo está descartado por el dominio — bloquear la
 * pantalla a alguien que conduce, y competir por la ÚNICA ranura de modal de
 * la app.
 *
 * Sin claves i18n propias: se reusan las que ya existen.
 */
export const DirectionChangeToast = ({
  onYes,
  onNo,
  disabled = false,
  ...props
}: ContainerProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Container data-testid="direction-change-toast" {...props}>
      <Question component="p" variant="medium" size="M">
        {t('modals:timerOverlay.directionChangeQuestion')}
      </Question>
      <Actions>
        <YesButton type="button" onClick={onYes} disabled={disabled}>
          {t('common:yes')}
        </YesButton>
        <NoButton type="button" onClick={onNo} disabled={disabled}>
          {t('common:no')}
        </NoButton>
      </Actions>
    </Container>
  );
};

export default DirectionChangeToast;
